/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Text } from 'foundations-components/components/Text';
import { Form } from 'foundations-components/transitional/components/Form';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { FORM_ACTION_IDS } from 'asset-actions-lib-forms/constants/actionIds';
import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import FormCloningWarningWithFlows from 'asset-actions-lib-forms/clone/components/FormCloningWarningWithFlows';
import FormNameValidationMessage from 'asset-actions-lib-forms/clone/components/FormNameValidationMessage';
import BusinessUnitSelector from 'asset-actions-lib-forms/clone/components/BusinessUnitSelector';
import useCloneForm from 'asset-actions-lib-forms/clone/hooks/useCloneForm';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_NAME_LENGTH = 255;
function getDefaultFormName(formName) {
  return I18n.text(`assetActionsLibForms.modals.cloneDialog.defaultFormName`, {
    originalFormName: I18n.SafeString(formName)
  });
}
function getIsNameValid(name) {
  return Boolean(name && name.length > 0 && name.length < MAX_NAME_LENGTH);
}
export default function CloneFormDialog({
  crmObject,
  onClose,
  scopes,
  onConfirm
}) {
  const {
    onActionError
  } = useAssetActions();
  const {
    cloneForm,
    loading,
    error,
    clearError
  } = useCloneForm();
  const hasBusinessUnitAccess = scopes.includes('business-units-access');
  const originalFormName = String(crmObject ? getPropertyValue(crmObject, 'hs_name', '') : '');
  const businessUnitId = crmObject ? getPropertyValue(crmObject, 'hs_all_assigned_business_unit_ids') : null;
  const formGuid = String(crmObject ? getPropertyValue(crmObject, 'hs_form_id', '') : '');
  const formType = String(crmObject ? getPropertyValue(crmObject, 'hs_form_type', '') : '');
  const embedVersion = String(crmObject ? getPropertyValue(crmObject, 'hs_embed_version', '') : '');
  const [updatedName, setUpdatedName] = useState(getDefaultFormName(originalFormName));
  const [updatedBusinessUnitId, setUpdatedBusinessUnitId] = useState(businessUnitId ? parseInt(`${businessUnitId}`, 10) : 0);
  const [errorCode, setErrorCode] = useState('');
  const isNameValid = getIsNameValid(updatedName);
  const hasErrors = Boolean(error || !isNameValid);
  const handleNameChange = event => {
    setUpdatedName(event.target.value);
    if (errorCode === 'DUPLICATE_NAME') {
      clearError();
      setErrorCode('');
    }
  };
  const handleSubmit = () => {
    return cloneForm({
      businessUnitId: updatedBusinessUnitId,
      formType: formType,
      embedVersion,
      guid: formGuid,
      name: updatedName
    }).then(clonedForm => {
      onConfirm(FORM_ACTION_IDS.CLONE, {
        clonedForm
      });
    }).catch(actionError => {
      if (!onActionError) {
        return;
      }
      onActionError({
        actionId: FORM_ACTION_IDS.CLONE,
        error: actionError
      });
      if (actionError.networkError && actionError.networkError.status) {
        const statusCode = actionError.networkError.status;
        switch (statusCode) {
          case 0:
            setErrorCode('NO_NETWORK');
            break;
          case 404:
            setErrorCode('NOT_FOUND');
            break;
          case 409:
            setErrorCode('DUPLICATE_NAME');
            break;
          default:
            setErrorCode('GENERIC_ERROR');
            break;
        }
      }
    });
  };
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "form-clone-dialog",
    onEsc: onClose,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.cloneDialog.header"
        })
      })]
    }), /*#__PURE__*/_jsx(Form, {
      onSubmit: handleSubmit,
      children: /*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(Text, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.introduction"
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.formNameLabel"
          }),
          error: hasErrors,
          validationMessage: /*#__PURE__*/_jsx(FormNameValidationMessage, {
            errorCode: errorCode,
            isNameValid: isNameValid
          }),
          children: /*#__PURE__*/_jsx(TextInput, {
            "data-test-id": "clone-form-name",
            value: updatedName,
            onChange: handleNameChange
          })
        }), hasBusinessUnitAccess ? /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.businessUnit_buv2"
          }),
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsx(BusinessUnitSelector, {
            onChange: val => setUpdatedBusinessUnitId(val || 0),
            value: updatedBusinessUnitId
          })
        }) : null, /*#__PURE__*/_jsx(FormCloningWarningWithFlows, {
          formGuid: formGuid,
          formType: formType,
          hasBusinessUnitAccess: hasBusinessUnitAccess,
          isExternalEditor: false
        })]
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        disabled: loading || hasErrors,
        onClick: handleSubmit,
        "data-test-id": "clone-form-button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.cloneDialog.cloneCta"
        })
      })
    })]
  });
}